import {Injectable} from "@angular/core";
import {UserTokens} from "../interfaces/domain/UserTokens";
import {map, Observable, of, Subject} from "rxjs";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {GlobalConfig} from "../configs/GlobalConfig";
import {AppEventBus} from "../event-buses/app.event-bus";
import {AppEventName} from "../event-buses/AppEventName";
import {AppEvent} from "../event-buses/AppEvent";
import {StringIndex} from "../interfaces/generic/StringIndex";

@Injectable({
  providedIn: 'root',
})
export class UserTokensRepository {

  private impersonatedTokens?: UserTokens;

  private readonly accessTokenRefreshTimeout = 5000;
  private accessTokenRefreshSubject?: Subject<any>;
  private accessTokenRefreshTimestamp = 0;
  private refreshedAccessTokenData?: any;

  constructor(private httpClient: HttpClient,
              private appEventBus: AppEventBus) {

  }

  setImpersonation(tokens: UserTokens) {
    this.impersonatedTokens = tokens;
  }

  removeImpersonation() {
    this.impersonatedTokens = undefined;
  }

  setUserTokens(userTokens: UserTokens) {
    if(userTokens) {
      if(this.impersonatedTokens) {
        this.impersonatedTokens = userTokens;
      }
      else {
        localStorage.setItem('userTokens', JSON.stringify(userTokens));
      }
    }
  }

  getUserTokens(): UserTokens {
    if(this.impersonatedTokens) {
      return this.impersonatedTokens;
    }
    else {
      const item = localStorage.getItem('userTokens');
      return JSON.parse(<string>item);
    }
  }

  updateAccessToken(accessToken: string) {
    const userTokens = this.getUserTokens();
    userTokens.accessToken = accessToken;
    this.setUserTokens(userTokens);
  }

  getAccessToken<T>(): Observable<T|null> {
    const userTokens = this.getUserTokens();

    if(!userTokens || !userTokens.accessToken) {
      return of(null);
    }

    const currentTime = new Date().getTime();

    if((currentTime - this.accessTokenRefreshTimestamp) < this.accessTokenRefreshTimeout) {
      return of(this.refreshedAccessTokenData);
    }
    else {
      if(!this.accessTokenRefreshSubject) {
        this.accessTokenRefreshSubject = new Subject();

        let refreshEndpoint = GlobalConfig.refreshAccessTokenEndpoint;

        if(userTokens && userTokens.sso) {
          refreshEndpoint = GlobalConfig.refreshSsoAccessTokenEndpoint;
        }

        this.httpClient.get<T>(refreshEndpoint, this.getRefreshTokenHeaders()).pipe(
          map((data) => {
            const indexData = <StringIndex>data;
            if(indexData && indexData['token']) {
              this.updateAccessToken(indexData['token']);
              this.appEventBus.dispatch(new AppEvent(AppEventName.NewAccessToken, this.getUserTokens()));
            }
            return data;
          })
        ).subscribe({
          next: (data) => {
            if(this.accessTokenRefreshSubject) {
              this.accessTokenRefreshSubject.next(data);
              this.accessTokenRefreshSubject.complete();
              this.accessTokenRefreshSubject = undefined;

              this.refreshedAccessTokenData = data;
              this.accessTokenRefreshTimestamp = new Date().getTime();
            }
          },
          error: (error) => {
            if(this.accessTokenRefreshSubject) {
              this.accessTokenRefreshSubject.error(error);
              this.accessTokenRefreshSubject.complete();
              this.accessTokenRefreshSubject = undefined;
            }
          },
          complete: () => {
            if(this.accessTokenRefreshSubject) {
              this.accessTokenRefreshSubject.complete();
              this.accessTokenRefreshSubject = undefined;
            }
          }
        });
      }

      return this.accessTokenRefreshSubject.asObservable();
    }
  }

   getRefreshTokenHeaders() {
    const userTokens = this.getUserTokens();
    let headers;

    if(userTokens && userTokens.sso) {
      headers = new HttpHeaders({'Authorization': 'Refresh ' + userTokens.accessToken});
    }
    else if(userTokens && userTokens.refreshToken && userTokens.refreshToken) {
      headers = new HttpHeaders({'Authorization': `Refresh ${userTokens.refreshToken}`});
    }
    else {
      headers = new HttpHeaders({'Authorization': '__invalid__'});
    }

    return {headers: headers};
  }

  clearLocalStorage(): void {
    localStorage.removeItem('userTokens');
  }
}
